import Stack from '@mui/material/Stack';
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { NavContext } from '../../../context/NavContext';
import axios from '../../../api/axios';
import { CompanyAWS } from '../../../@types/AWS';
import { toast } from 'react-toastify';
import Card from '../../common/Card/Card';
import { SubHeading } from '../../common/PageHeadings/PageHeadings';
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Skeleton, Typography, Tooltip, IconButton, List, ListItem, ListItemText } from '@mui/material';
import Button from '../../common/Button';
import { Edit } from '@mui/icons-material';
import EditAttributesModal from '../EditAttributesModal/EditAttributesModal';
import CreateLicenseModal from '../CreateLicenseModal/CreateLicenseModal';
import DeleteIcon from '@mui/icons-material/Delete';
import { License } from '../../../@types/Company';
import EditLicenseModal from '../EditLicenseModal/EditLicenseModal';
import LeadsList from '../../LeadsList/LeadsList';
import DealerUserList from '../../DealerUserList/DealerUserList';
import LeadStatistics from '../../LeadStatistics/LeadStatistics';
import AddressBookEdit from '../../DealerProfilePage/AddressBookEdit/AddressBookEdit';
import moment from 'moment';
import DealerWarrantyRegistrations from '../../DealerWarrantyRegistrations/DealerWarrantyRegistrations';

interface AdminDealerProps { }

const AdminDealer: FC<AdminDealerProps> = () => {
  const [company, setCompany] = useState<CompanyAWS>();
  const [loading, setLoading] = useState<boolean>(true);
  const params = useParams();
  const dealerId = params.id ?? '';
  const navContextData = useContext(NavContext);

  const fetchDealerData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/dealer/meta-data', { params: { dealerId: dealerId } });
      const company = response.data.data as CompanyAWS;
      setCompany(company);
      navContextData?.setRouteDisplayText(`${company.attributes.companyName} (${dealerId})`);
    } catch (error) {
      toast.error(`Server error loading dealer with dealerId: ${dealerId}`);
    } finally {
      setLoading(false);
    }
  }, [dealerId, setLoading, setCompany]);

  return (
    <Stack spacing={6}>
      <DealerAttributes
        company={company}
        loading={loading}
        fetchDealerData={fetchDealerData} />
      <AddressBookEdit
        dealerId={dealerId} />
      <LicenseList
        company={company}
        loading={loading}
        fetchDealerData={fetchDealerData} />
      <DealerWarrantyRegistrations dealerId={dealerId} />
      <DealerUserList
        dealerId={dealerId} />
      <DealerLeads
        dealerId={dealerId} />
      <DealerLeadStatistics dealerId={dealerId} />
    </Stack>
  )
};

interface DealerLeadStatisticsProps {
  dealerId: string
}

const DealerLeadStatistics: FC<DealerLeadStatisticsProps> = ({ dealerId }) => {
  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between">
        <SubHeading>Dealer Lead Statistics</SubHeading>
      </Stack>
      <LeadStatistics dealerId={dealerId} />
    </Stack>
  )
}

interface DealerLeadsProps {
  dealerId: string
}

const DealerLeads: FC<DealerLeadsProps> = ({ dealerId }) => {
  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between">
        <SubHeading>Dealer Leads</SubHeading>
      </Stack>
      <LeadsList dealerId={dealerId} pageSize={10} />
    </Stack >
  )
}

interface DealerAttributesProps {
  company: CompanyAWS | undefined;
  loading: boolean;
  fetchDealerData: () => void;
}

const DealerAttributes: FC<DealerAttributesProps> = ({ company, loading, fetchDealerData }) => {
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false);

  useEffect(() => {
    if (!editModalVisible) {
      fetchDealerData();
    }
  }, [editModalVisible, fetchDealerData]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between">
        <SubHeading>Dealer Attributes</SubHeading>
        <Button onClick={() => setEditModalVisible(true)}>Edit Attributes</Button>
      </Stack>
      <Card>
        {!loading ?
          (
            <Stack spacing={2}>
              <List
                sx={{
                  padding: '0px 8px 0px 8px',
                  overflowWrap: 'break-word',
                  '.MuiListItem-root': {
                    paddingLeft: 0,
                    paddingRight: 0,
                    '.MuiListItemText-root': {
                      lineHeight: '20px',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                    },
                    '.MuiListItemText-root:first-of-type': {
                      marginRight: 12,
                    },
                    '.MuiListItemText-root:last-of-type': {
                      textAlign: 'right',
                    }
                  }
                }}>
                <ListItem>
                  <ListItemText>Dealer Name</ListItemText>
                  <ListItemText>{company?.attributes.companyName}</ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>Date Created</ListItemText>
                  <ListItemText>{moment(new Date(company?.attributes?.dateCreated ?? '')).format('MM/DD/YYYY')}</ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>Active / Inactive</ListItemText>
                  <ListItemText>{company?.attributes.active ? 'Active' : 'Inactive'}</ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>Allow Leads</ListItemText>
                  <ListItemText>{company?.attributes.allowLeads ? 'Allowed' : 'Not Allowed'}</ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>Lead Priority Dealer</ListItemText>
                  <ListItemText>{company?.attributes.priorityDealer ? 'Priority' : 'Standard'}</ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>International / Domestic</ListItemText>
                  <ListItemText>{company?.attributes.international ? 'International' : 'Domestic'}</ListItemText>
                </ListItem>
              </List>
            </Stack>
          )
          :
          (<DealerAttributesLoading />)
        }
      </Card >
      {company !== undefined &&
        <EditAttributesModal
          company={company}
          closeModal={() => setEditModalVisible(false)}
          isVisible={editModalVisible} />
      }
    </Stack >
  )
}

const DealerAttributesLoading = () => {
  return (
    <Stack spacing={2}>
      <List sx={{
        padding: '0px 8px 0px 8px',
        overflowWrap: 'break-word',
        '.MuiListItem-root': {
          paddingLeft: 0,
          paddingRight: 0,
          '.MuiListItemText-root': {
            lineHeight: '20px',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          },
          '.MuiListItemText-root:first-of-type': {
            marginRight: 12,
          },
          '.MuiListItemText-root:last-of-type': {
            textAlign: 'right',
          }
        }
      }}>
        <ListItem>
          <ListItemText>Dealer Name</ListItemText>
          <ListItemText><Skeleton animation="wave" width={'100%'} height={20} /></ListItemText>
        </ListItem>
        <ListItem>
          <ListItemText>Date Created</ListItemText>
          <ListItemText><Skeleton animation="wave" width={'100%'} height={20} /></ListItemText>
        </ListItem>
        <ListItem>
          <ListItemText>Active / Inactive</ListItemText>
          <ListItemText><Skeleton animation="wave" width={'100%'} height={20} /></ListItemText>
        </ListItem>
        <ListItem>
          <ListItemText>Allow Leads</ListItemText>
          <ListItemText><Skeleton animation="wave" width={'100%'} height={20} /></ListItemText>
        </ListItem>
        <ListItem>
          <ListItemText>Lead Priority Dealer</ListItemText>
          <ListItemText><Skeleton animation="wave" width={'100%'} height={20} /></ListItemText>
        </ListItem>
        <ListItem>
          <ListItemText>International / Domestic</ListItemText>
          <ListItemText><Skeleton animation="wave" width={'100%'} height={20} /></ListItemText>
        </ListItem>
      </List>
    </Stack>
  )
}


const licenseTableHeaders = ['state', 'expiration date', 'type', 'status', '']

interface LicenseListProps {
  company: CompanyAWS | undefined;
  loading: boolean;
  fetchDealerData: () => void;
}

const LicenseList: FC<LicenseListProps> = ({ company, loading, fetchDealerData }) => {
  const [createModalVisible, setCreateModalVisible] = useState<boolean>(false);
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false);
  const [licenseToEdit, setLicenseToEdit] = useState<License | undefined>();
  const [licensetoEditIndex, setLicensetoEditIndex] = useState<number>(0);

  useEffect(() => {
    if (!createModalVisible && !editModalVisible) fetchDealerData();
  }, [createModalVisible, editModalVisible, fetchDealerData]);

  const editLicense = async (license: License, index: number) => {
    setLicenseToEdit(license);
    setLicensetoEditIndex(index);
    setEditModalVisible(true);
  }

  const deleteLicense = async (index: number) => {
    try {
      if (company?.attributes.licenses) {
        const updatedCompany: CompanyAWS | undefined = company ? { ...company } : undefined;
        if (updatedCompany?.attributes.licenses) {
          const licensesToKeep = updatedCompany.attributes.licenses.filter((license, i) => i !== index);
          updatedCompany.attributes.licenses = licensesToKeep;
        }
        await axios.put(`/dealer/meta-data`, updatedCompany);
        fetchDealerData();
        toast.success(`Removed license`);

      }
    } catch (error) {
      console.log(error);
      toast.error('Server Error');
    }
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between">
        <SubHeading>Licenses</SubHeading>
        <Button onClick={() => setCreateModalVisible(true)}>Add License</Button>
      </Stack>
      <Card>
        {!loading ?
          (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {licenseTableHeaders.map((header, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          ...((header === 'status'
                            || header === 'expiration date'
                            || header === 'type') && {
                            textAlign: 'center'
                          }),
                          borderLeft: 'none',
                          borderRight: 'none',
                          borderTop: 'none',
                          textTransform: 'uppercase'
                        }} >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {company?.attributes.licenses?.length ?
                    (company?.attributes.licenses?.map((license, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '.MuiTableCell-root': {
                            border: 'none',
                            paddingTop: '12px',
                            paddingBottom: '12px'
                          }
                        }}>
                        <TableCell>
                          {license.state}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'center'
                          }}>
                          {license.expirationDate}
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'center'
                          }}>
                          {license.type}
                        </TableCell>
                        <TableCell
                          sx={{
                            textTransform: 'uppercase',
                            textAlign: 'center'
                          }}>
                          {license.status}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" justifyContent="flex-end">
                            <Tooltip title="Edit License" disableInteractive>
                              <IconButton onClick={() => editLicense(license, index)}>
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete License" disableInteractive>
                              <IconButton onClick={() => deleteLicense(index)}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )))
                    :
                    (
                      <TableRow
                        sx={{
                          '.MuiTableCell-root': {
                            border: 'none',
                            paddingTop: '12px',
                            paddingBottom: '12px'
                          }
                        }}>
                        <TableCell colSpan={licenseTableHeaders.length + 1}>
                          <Stack
                            direction="column"
                            alignItems="center"
                            justifyContent="center"
                            spacing={2}
                            sx={{
                              marginTop: '20px',
                              marginBottom: '20px',
                              textTransform: 'uppercase',
                              textAlign: 'center'
                            }}>
                            <Typography>No Licenses</Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )
                  }

                </TableBody>
                {company !== undefined &&
                  <>
                    <CreateLicenseModal company={company} closeModal={() => setCreateModalVisible(false)} isVisible={createModalVisible} />
                    <EditLicenseModal company={company} license={licenseToEdit} index={licensetoEditIndex} closeModal={() => setEditModalVisible(false)} isVisible={editModalVisible} />
                  </>
                }
              </Table>
            </TableContainer>
          )
          :
          (<LicenseListLoading />)
        }
      </Card>
    </Stack>
  )
}

const LicenseListLoading = () => {
  const displayCount = 5;
  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {licenseTableHeaders.map((header, index) => (
              <TableCell
                key={index}
                sx={{
                  ...(header === 'active / inactive' && {
                    textAlign: 'center'
                  }),
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderTop: 'none',
                  textTransform: 'uppercase'
                }}>
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array(displayCount).fill(null).map((value, index) => (
            <TableRow
              key={index}
              sx={{
                '.MuiTableCell-root': {
                  border: 'none',
                  paddingTop: '12px',
                  paddingBottom: '12px'
                }
              }}>
              <TableCell>
                <Skeleton animation="wave" width={'100%'} height={20} />
              </TableCell>
              <TableCell>
                <Skeleton animation="wave" width={'100%'} height={20} />
              </TableCell>
              <TableCell>
                <Skeleton animation="wave" width={'100%'} height={20} />
              </TableCell>
              <TableCell>
                <Skeleton animation="wave" width={'100%'} height={20} />
              </TableCell>
              <TableCell>
                <Stack direction="row" justifyContent="flex-end" spacing={2}>
                  <Skeleton animation="wave" width={24} height={24} />
                  <Skeleton animation="wave" width={24} height={24} />
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default AdminDealer;
